import { GuardJobDetails, GuardJobShiftItem } from '../../../../../types';

/**
 * Request object for cancelling guard job shifts.
 *
 * This class is used to create a request to cancel one or more shifts for a job.
 * For regular jobs, use shiftIDs. For repeated jobs, use repeatedShiftIDs.
 */
export class CancelGuardJobShiftsRequest {
    /**
     * Creates a new instance of CancelGuardJobShiftsRequest.
     *
     * @param jobID - The ID of the job to cancel shifts for
     * @param shiftIDs - Optional array of shift IDs to cancel for regular jobs
     * @param repeatedShiftIDs - Optional array of shift IDs to cancel for repeated jobs
     * @param cancelReason - Optional string input for guards to add a reason for cancellation
     * @throws Error if jobID is not provided or if neither shiftIDs nor repeatedShiftIDs is provided
     */
    constructor(
        public readonly jobID: number,
        public readonly shiftIDs?: number[],
        public readonly repeatedShiftIDs?: number[],
        public readonly cancelReason?: string
    ) {
        if (!jobID) {
            throw new Error('Job ID is required');
        }

        if (!shiftIDs && !repeatedShiftIDs) {
            throw new Error('Either shift IDs or repeated shift IDs are required');
        }
    }

    /**
     * Factory method to create a CancelGuardJobShiftsRequest from a job and shifts.
     *
     * This method automatically determines whether to use shiftIDs or repeatedShiftIDs
     * based on whether the job is a repeated job.
     *
     * @param job - The job details containing information about the job
     * @param shifts - The shifts to cancel
     * @param cancelReason - Optional string for guard's reason for cancellation
     * @returns A new CancelGuardJobShiftsRequest instance
     */
    static createFromJobAndShifts(
        job: GuardJobDetails,
        shifts: GuardJobShiftItem[],
        cancelRepeat: boolean,
        cancelReason?: string
    ) {
        const isRepeatedJob = !!job.repeat_cycle && cancelRepeat;
        const shiftIDsList = shifts.map(shift => shift.id);

        return new CancelGuardJobShiftsRequest(
            job.id,
            isRepeatedJob ? undefined : shiftIDsList,
            isRepeatedJob ? shiftIDsList : undefined,
            cancelReason
        );
    }

    /**
     * Converts the instance data into a plain JavaScript object representation to send to the server.
     *
     * @return {Object} An object containing `shift_ids` and `repeated_shift_ids` properties derived from the instance.
     */
    toJSON(): object {
        return {
            shift_ids: this.shiftIDs,
            repeated_shift_ids: this.repeatedShiftIDs,
            guard_cancel_reason: this.cancelReason
        };
    }
}
